"use client";

import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchNamespaces } from "@/lib/api-client";
import { queryKeys } from "@/lib/query/keys";
import { useNamespaceStore } from "@/state/namespace-store";
import type { NamespaceSummary } from "@/types/namespaces";

export function useNamespacesQuery() {
  return useQuery({
    queryKey: queryKeys.namespaces(),
    queryFn: fetchNamespaces,
    staleTime: 60_000,
  });
}

export function useActiveNamespaceId() {
  return useNamespaceStore((state) => state.selectedNamespaceId);
}

/**
 * Returns the URL slug of the currently selected namespace, or null when no
 * namespace is selected (or the namespaces list hasn't loaded yet). Slugs are
 * what route links use — combine with WORKSPACE_ROUTE_TEMPLATES to build
 * navigation hrefs.
 */
export function useActiveNamespaceSlug(): string | null {
  const selectedId = useNamespaceStore((state) => state.selectedNamespaceId);
  const namespacesQuery = useNamespacesQuery();
  if (!selectedId) return null;
  const match = namespacesQuery.data?.find((n) => n.namespaceId === selectedId);
  return match?.slug ?? null;
}

export function useNamespaceSelectionSync(namespaces?: NamespaceSummary[]) {
  const synchronizeSelection = useNamespaceStore((state) => state.synchronizeSelection);

  useEffect(() => {
    if (!namespaces?.length) {
      return;
    }

    synchronizeSelection(namespaces);
  }, [namespaces, synchronizeSelection]);
}

